// CIM Command Center → Ledger tab (#/cim/<engagementId>?tab=ledger) — the
// facts ledger of one deal (routes/cim_ledger.py, design §12, PR 3c).
//
//   No ledger yet   Codename · Vertical · Data cutoff, the vault's counts, and
//                   Extract (only with can_run, a codename and a vertical)
//   Job running     progress lines polled every 5 s (window.usePolling,
//                   cacheMs: 0) and Cancel; the tab gets a dot
//   Job failed      the code-written error and Extract again
//   Ledger          snapshot selector, the update_available chips, Extract
//                   changes / Extract all, Freeze snapshot; sub-tabs
//                   Statements (a grid per catalog group, months in one
//                   "Monthly" group; Approve clean, Approve per management
//                   (confirmed) and Override not-Ready send the explicit ids
//                   the server listed) and Discrepancies (pick,
//                   sides, accept, pick-statement-for-all, reopen)
//   Review bar      Approve clean (every statement: GET /ledger/clean, a
//                   confirm with the counts per statement, bulk approve in
//                   chunks of 5,000, scope clean) and what is left for a
//                   person: Conflicts (opens the queue), per management, file
//                   not Ready, one at a time
//   Fact drawer     value, chips, source, locator, quote ("Per management"),
//                   other candidates, D-codes, history; the §8.2 actions the
//                   server says are open, each with its Reason where needed; opened
//                   from a snapshot, the status and approver that version froze
//   Freeze dialog   counts, blocks, warnings, the QoE choice, acknowledge
//
// Every figure arrives formatted by the server (cim_book's fmt). There is no
// value input anywhere: a person approves, overrides with a reason, disputes,
// rejects, reopens, keeps or picks — never types a number. Review actions show
// only with can_review, Extract only with can_run. Labels only; no spend.
//
// window.CimLedgerTab({ engagementId, phone, onJobChange, initialSub }) — initialSub 'discrepancies'
// opens on the conflicts list (the Overview's Settle conflicts)
// window.CimFactDrawer — the fact drawer (the Sections tab opens it read-only)
// window.CimLedgerKit — the pure rules below (atlas-crm/test/cim_ledger.test.mjs)

const LG_STATUS = {
  candidate: ['Unconfirmed', 'slate'], approved: ['Approved', 'green'], overridden: ['Overridden', 'violet'],
  disputed: ['Disputed', 'rose'], rejected: ['Rejected', 'slate'], superseded: ['Superseded', 'slate'],
};
const LG_JOB = {
  queued: ['Queued', 'slate'], running: ['Running', 'blue'], validating: ['Validating', 'amber'],
  completed: ['Completed', 'green'], failed: ['Failed', 'rose'], cancelled: ['Cancelled', 'slate'],
};
const LG_DCODE = { open: ['Open', 'rose'], resolved: ['Resolved', 'green'], accepted: ['Accepted', 'teal'], obsolete: ['Obsolete', 'slate'] };
const LG_KIND_HUE = { conflict: 'rose', identity: 'amber', tie: 'amber', reconcile: 'blue', difference: 'slate', manual: 'violet' };
const LG_ACTIVE = new Set(['queued', 'running', 'validating']);
const LG_TERMINAL = new Set(['completed', 'failed', 'cancelled']);
const LG_EVENT = {
  status: ['Status', 'var(--text-2)'], tool: ['Read', 'var(--text-3)'], save: ['Saved', 'var(--green-fg)'],
  check: ['Check', 'var(--amber-fg)'],
};
const LG_TONE = {
  candidate: { fg: 'var(--text)', bg: 'var(--slate-bg)' },
  approved: { fg: 'var(--green-fg)', bg: 'var(--green-bg)' },
  overridden: { fg: 'var(--violet-fg)', bg: 'var(--violet-bg)' },
  disputed: { fg: 'var(--rose-fg)', bg: 'var(--rose-bg)' },
  rejected: { fg: 'var(--text-4)', bg: 'transparent' },
  superseded: { fg: 'var(--text-4)', bg: 'transparent' },
};
// Worst first: what a collapsed "Monthly" cell shows for its row.
const LG_WORST = ['disputed', 'candidate', 'overridden', 'approved', 'rejected', 'superseded'];
const LG_ACTION_LABELS = { approve: 'Approve', override: 'Override', dispute: 'Dispute', reject: 'Reject', reopen: 'Reopen', keep: 'Keep' };
const LG_DONE = { approve: 'Approved', override: 'Overridden', dispute: 'Disputed', reject: 'Rejected', reopen: 'Reopened', keep: 'Kept' };
const LG_EVENT_ACTION = {
  created: 'Read', approved: 'Approved', overridden: 'Overridden', disputed: 'Disputed', rejected: 'Rejected',
  reopened: 'Reopened', superseded: 'Superseded', flagged: 'Newer data', unflagged: 'Kept', resolved: 'Resolved',
};
const LG_COUNT_LABELS = { rows_read: 'Rows read', matched: 'Matched', blank: 'Blank', unparsed: 'Unparsed' };
const LG_FREEZE_COUNTS = [
  ['approved', 'Approved'], ['candidates', 'Unconfirmed'], ['open_dcodes', 'Open conflicts'], ['flagged', 'Newer data'],
  ['missing_p0', 'Missing P0 blocks'],
];
const LG_MIN_REASON = 3;
const LG_HELD_PREVIEW = 6;

const _lgCard = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10 };
const _lgCap = { fontSize: 10.5, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-4)' };
const _lgInput = {
  width: '100%', fontSize: 12.5, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)',
  background: 'var(--surface)', color: 'var(--text)', boxSizing: 'border-box', fontFamily: 'inherit',
};
const _lgLink = { border: 'none', background: 'transparent', padding: 0, font: 'inherit', color: 'var(--accent-text)', cursor: 'pointer', textAlign: 'left' };
const _lgMono = { fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-3)' };

// ── pure rules (window.CimLedgerKit) ───────────────────────────────────────

// apiFetch throws "409 Conflict: {"detail": …}". A string detail is the
// server's sentence; an object (Freeze's {blocks, warnings}) comes back whole.
const _lgErr = (e) => {
  const msg = (e && e.message) || '';
  const m = /^(\d{3})\b[^:]*:\s*([\s\S]*)$/.exec(msg);
  if (!m) return { status: null, detail: msg || 'Something went wrong', body: null };
  let detail = m[2].trim();
  let body = null;
  try {
    const j = JSON.parse(detail);
    if (j && j.detail != null) {
      if (typeof j.detail === 'string') detail = j.detail;
      else if (Array.isArray(j.detail)) detail = (j.detail[0] && j.detail[0].msg) || 'Not valid';
      else { body = j.detail; detail = j.detail.message || 'Not yet'; }
    }
  } catch (_) { /* not JSON */ }
  return { status: Number(m[1]), detail: detail || `Error ${m[1]}`, body };
};

const _lgReasonOk = (r) => String(r || '').trim().length >= LG_MIN_REASON;
const _lgPlural = (n, one, many) => `${n} ${n === 1 ? one : (many || `${one}s`)}`;
const _lgShortName = (n) => {
  const p = String(n || '').trim().split(/\s+/).filter(Boolean);
  return p.length > 1 ? `${p[0][0]}. ${p.slice(1).join(' ')}` : (p[0] || '');
};
const _lgDay = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
const _lgWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const t = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return d.toDateString() === new Date().toDateString() ? t : `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${t}`;
};
const _lgClock = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit' });
};
const _lgCap1 = (s) => { const t = String(s || ''); return t.charAt(0).toUpperCase() + t.slice(1); };

// Why Extract is off, as a label, or null when it may run.
const lgExtractGate = (L) => {
  if (!L) return { ok: false, why: null };
  if (!L.enabled) return { ok: false, why: 'Switched off' };
  if (!L.can_run) return { ok: false, why: 'Deal team or admin' };
  const s = L.settings || {};
  if (!String(s.codename || '').trim() || !s.vertical) return { ok: false, why: 'Codename and vertical first' };
  return { ok: true, why: null };
};

const lgBulkSummary = (res) => {
  const r = res || {};
  const skipped = Object.values(r.skipped || {}).reduce((a, b) => a + (Number(b) || 0), 0);
  return skipped ? `Approved ${r.approved || 0} · Skipped ${skipped}` : `Approved ${r.approved || 0}`;
};
const lgSkipDetail = (res) => Object.entries((res && res.skipped) || {}).filter(([, n]) => n)
  .map(([why, n]) => `${n} ${why}`).join(' · ');

const lgUpdateLines = (u) => {
  if (!u) return [];
  const out = [];
  if (u.files_added) out.push(`${_lgPlural(u.files_added, 'file')} added`);
  if (u.files_removed) out.push(`${_lgPlural(u.files_removed, 'file')} removed`);
  if (u.files_changed) out.push(`${_lgPlural(u.files_changed, 'file')} changed`);
  if (u.notes) out.push(_lgPlural(u.notes, 'note'));
  return out;
};
const lgSnapshotLines = (u) => {
  if (!u) return [];
  const out = [];
  if (u.added) out.push(`${u.added} approved since`);
  if (u.removed) out.push(`${u.removed} no longer approved`);
  if (u.changed) out.push(`${_lgPlural(u.changed, 'override or approver', 'overrides or approvers')} changed`);
  if (u.flagged) out.push(`${_lgPlural(u.flagged, 'locked figure')} flagged`);
  return out;
};

const lgSnapshotLabel = (s) => [`Locked numbers v${s.version}`, _lgDay(s.frozen_at), s.frozen_by ? _lgShortName(s.frozen_by) : '']
  .filter(Boolean).join(' · ');

// The drawer's buttons from the server's {action: null | why-not}. Override
// shows only when the one thing stopping Approve is that it needs one.
const lgVisibleActions = (actions) => {
  const a = actions || {};
  const open = (k) => Object.prototype.hasOwnProperty.call(a, k) && a[k] == null;
  const out = [];
  if (open('approve')) out.push({ key: 'approve', label: 'Approve', reason: false, primary: true });
  if (open('override') && !open('approve')) out.push({ key: 'override', label: 'Override', reason: true, primary: true });
  ['dispute', 'reject', 'reopen', 'keep'].forEach((k) => {
    if (open(k)) out.push({ key: k, label: LG_ACTION_LABELS[k], reason: true, primary: k === 'keep' });
  });
  return out;
};
const lgApproveWhy = (actions) => {
  const w = actions && actions.approve;
  return w ? _lgCap1(w) : null;
};

// Grid columns: every non-month period, then the months — one "Monthly"
// column while collapsed, every month while open.
const lgColumns = (periods, monthlyOpen) => {
  const ps = periods || [];
  const plain = ps.filter((p) => !p.monthly).map((p) => ({ kind: 'period', ...p }));
  const months = ps.filter((p) => p.monthly);
  if (!months.length) return plain;
  if (monthlyOpen) return [...plain, ...months.map((p, i) => ({ kind: 'period', ...p, firstMonth: i === 0 }))];
  return [...plain, { kind: 'monthly', period: '__monthly', label: 'Monthly', count: months.length, periods: months.map((p) => p.period) }];
};
const lgMonthlySummary = (row, periods) => {
  const cells = (periods || []).map((p) => row.cells && row.cells[p]).filter(Boolean);
  if (!cells.length) return null;
  const worst = LG_WORST.find((s) => cells.some((c) => c.status === s)) || 'candidate';
  return { n: cells.length, status: worst, flag: cells.some((c) => c.flag) };
};

// Reconcile D-codes by check, for "Pick statement for all shown".
const lgReconcileGroups = (items) => {
  const out = {};
  (items || []).forEach((d) => {
    if (d.kind !== 'reconcile' || !d.open) return;
    const k = d.check || 'reconcile';
    (out[k] = out[k] || []).push(d.code);
  });
  return Object.entries(out).filter(([, codes]) => codes.length > 1).map(([check, codes]) => ({ check, codes }));
};

const lgMergeEvents = (cur, next) => {
  if (!next || !next.length) return cur;
  const seen = new Set(cur.map((e) => e.id));
  const out = cur.concat(next.filter((e) => !seen.has(e.id)));
  return out.length > 1500 ? out.slice(-1500) : out;
};

// Only the settings fields that changed (PATCH sends what it changes).
const lgSettingsPatch = (cur, draft) => {
  const c = cur || {};
  const out = {};
  const code = String(draft.codename || '').trim();
  if (code !== String(c.codename || '')) out.codename = code || null;
  if ((draft.vertical || null) !== (c.vertical || null)) out.vertical = draft.vertical || null;
  if ((draft.data_cutoff || null) !== (c.data_cutoff || null)) out.data_cutoff = draft.data_cutoff || null;
  return out;
};

// The grid the tab may show and act on: only one whose statement and snapshot are the ones
// selected (a switch keeps the previous grid until the new one lands).
const lgGridFor = (grid, statement, snapshot) => {
  if (!grid || grid.statement !== statement) return null;
  const v = grid.snapshot == null ? null : grid.snapshot;
  return v === (snapshot == null ? null : snapshot) ? grid : null;
};

// The More menu's arrow keys: the item to focus next (wrapping), Home / End; null = not ours.
const lgMenuStep = (n, i, key) => {
  if (!n) return null;
  if (key === 'Home') return 0;
  if (key === 'End') return n - 1;
  if (key === 'ArrowDown') return i < 0 ? 0 : (i + 1) % n;
  if (key === 'ArrowUp') return i < 0 ? n - 1 : (i - 1 + n) % n;
  return null;
};

// Review by exception. Bulk approve takes at most 5,000 ids a call: Approve clean sends in chunks.
const LG_BULK_MAX = 5000;
const lgChunks = (ids, n = LG_BULK_MAX) => {
  const out = [];
  for (let i = 0; i < (ids || []).length; i += n) out.push(ids.slice(i, i + n));
  return out;
};
// Several bulk results as one (the toast's summary and skip detail).
const lgMergeBulk = (results) => (results || []).reduce((acc, r) => {
  const out = { approved: acc.approved + ((r && r.approved) || 0), skipped: { ...acc.skipped } };
  Object.entries((r && r.skipped) || {}).forEach(([k, n]) => { out.skipped[k] = (out.skipped[k] || 0) + (Number(n) || 0); });
  return out;
}, { approved: 0, skipped: {} });
// The confirm's one line: what Approve clean takes, per statement.
const lgCleanLine = (r) => ((r && r.by_statement) || []).filter((b) => b.clean)
  .map((b) => `${b.label} ${b.clean.toLocaleString()}`).join(' · ');
// What stays for a person after Approve clean, worst first; zeros left out. Conflicts count the
// open queue (what a person works through); the rest count figures.
const lgLeftItems = (review, dOpen) => {
  const l = (review && review.left) || {};
  return [
    { key: 'conflicts', label: 'Conflicts', n: dOpen || 0, tip: `${_lgPlural(l.conflicting || 0, 'figure')} wait on a conflict` },
    { key: 'per_management', label: 'Per management', n: l.per_management || 0, tip: 'Per management, estimated or outside research: not read from a document. Approve them per statement' },
    { key: 'not_ready', label: 'File not Ready', n: l.not_ready || 0, tip: 'Mark the file Ready for CIM, or override per statement with a reason' },
    { key: 'one_by_one', label: 'One at a time', n: l.one_by_one || 0, tip: 'Low confidence, a flag on the reading, a removed file, or a calculation waiting on other figures' },
  ].filter((x) => x.n > 0);
};

window.CimLedgerKit = Object.freeze({
  err: _lgErr, reasonOk: _lgReasonOk, extractGate: lgExtractGate, bulkSummary: lgBulkSummary, skipDetail: lgSkipDetail,
  updateLines: lgUpdateLines, snapshotLines: lgSnapshotLines, snapshotLabel: lgSnapshotLabel, shortName: _lgShortName,
  visibleActions: lgVisibleActions, approveWhy: lgApproveWhy, columns: lgColumns, monthlySummary: lgMonthlySummary,
  reconcileGroups: lgReconcileGroups, mergeEvents: lgMergeEvents, settingsPatch: lgSettingsPatch, gridFor: lgGridFor,
  menuStep: lgMenuStep, chunks: lgChunks, mergeBulk: lgMergeBulk, cleanLine: lgCleanLine, leftItems: lgLeftItems,
});

// ── small pieces ───────────────────────────────────────────────────────────

const LgPill = ({ map, k, dot = false }) => {
  const [label, hue] = map[k] || [_lgCap1(String(k || '—').replace(/_/g, ' ')), 'slate'];
  return <window.Pill hue={hue} sz="sm" dot={dot}>{label}</window.Pill>;
};

const LgEmpty = ({ children }) => (
  <div style={{ ..._lgCard, padding: '18px 16px', fontSize: 12.5, color: 'var(--text-3)', textAlign: 'center' }}>{children}</div>
);

const LgReason = ({ value, onChange, autoFocus = false, phone }) => (
  <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} autoFocus={autoFocus} placeholder="Reason"
    aria-label="Reason" maxLength={2000}
    style={{ ..._lgInput, resize: 'vertical', minHeight: phone ? 64 : 52, fontSize: phone ? 15 : 12.5 }} />
);

const LgKV = ({ label, children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '110px minmax(0, 1fr)', gap: 8, padding: '4px 0', fontSize: 12.5, alignItems: 'baseline' }}>
    <span style={{ color: 'var(--text-4)' }}>{label}</span>
    <span style={{ color: 'var(--text)', overflowWrap: 'anywhere', minWidth: 0 }}>{children}</span>
  </div>
);

const LgSection = ({ title, children, right }) => (
  <div style={{ marginTop: 16 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      <span style={_lgCap}>{title}</span>
      <span style={{ flex: 1 }} />
      {right}
    </div>
    {children}
  </div>
);

// A figure's row in a list: value, status, source, locator. Opens that fact.
// On a phone the words go under the value and the chips, full width.
const LgFactLine = ({ f, onOpen, extra, phone }) => {
  const value = (
    <button type="button" onClick={() => onOpen && onOpen(f.id)}
      style={{ ..._lgLink, fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'var(--text)', minWidth: 96, flexShrink: 0,
        fontSize: phone ? 14 : undefined }}>{f.display}</button>
  );
  const words = (
    <div style={{ flex: 1, minWidth: 0, fontSize: phone ? 12.5 : 12, color: 'var(--text-3)', lineHeight: 1.45 }}>
      <div style={{ color: 'var(--text-2)', overflowWrap: 'anywhere' }}>{f.label}</div>
      <div style={{ overflowWrap: 'anywhere' }}>{[f.source, f.locator].filter(Boolean).join(' · ')}</div>
    </div>
  );
  const chips = (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end', flexShrink: 0 }}>
      {f.mgmt ? <window.Pill hue="teal" sz="sm">Per management</window.Pill>
        : f.origin && f.origin !== 'Document' ? <window.Pill hue="slate" sz="sm">{f.origin}</window.Pill> : null}
      <LgPill map={LG_STATUS} k={f.status} />
      {extra}
    </div>
  );
  if (phone) {
    return (
      <div style={{ padding: '10px 12px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>{value}<span style={{ flex: 1 }} />{chips}</div>
        {words}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px', borderTop: '1px solid var(--border)' }}>
      {value}{words}{chips}
    </div>
  );
};

// ── the grid ───────────────────────────────────────────────────────────────

const LgCell = ({ cell, onOpen, phone }) => {
  if (!cell) return <span style={{ color: 'var(--text-4)' }}>—</span>;
  const tone = LG_TONE[cell.status] || LG_TONE.candidate;
  const tip = [(LG_STATUS[cell.status] || [cell.status])[0], cell.dcode, cell.mgmt ? 'Per management' : null,
    cell.flag ? 'Newer data' : null, cell.sources > 1 ? `${cell.sources} sources` : null, cell.conflict ? 'Sources disagree' : null]
    .filter(Boolean).join(' · ');
  return (
    <button type="button" onClick={() => onOpen(cell.fact_id)} title={tip} aria-label={`${cell.display} · ${tip}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', cursor: 'pointer', padding: phone ? '5px 7px' : '3px 6px',
        borderRadius: 5, background: tone.bg, color: tone.fg, fontSize: phone ? 13 : 12, fontVariantNumeric: 'tabular-nums',
        fontWeight: cell.status === 'approved' || cell.status === 'overridden' ? 600 : 500, whiteSpace: 'nowrap',
        textDecoration: cell.status === 'rejected' ? 'line-through' : 'none', fontFamily: 'inherit' }}>
      {cell.flag && <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--amber-fg)', flexShrink: 0 }} />}
      {cell.display}
      {cell.mgmt && <span aria-hidden="true" style={{ fontSize: 9, fontWeight: 700, color: 'var(--teal-fg)' }}>PM</span>}
      {cell.sources > 1 && (
        <span aria-hidden="true" style={{ fontSize: 9.5, fontWeight: 700, padding: '0 4px', borderRadius: 999,
          background: cell.conflict ? 'var(--rose-fg)' : 'var(--surface)', color: cell.conflict ? 'var(--surface)' : 'var(--text-3)',
          border: '1px solid var(--border)' }}>{cell.sources}</span>
      )}
    </button>
  );
};

const LgGrid = ({ grid, monthlyOpen, onToggleMonthly, onOpen, phone }) => {
  const cols = lgColumns(grid.periods, monthlyOpen);
  const months = (grid.periods || []).filter((p) => p.monthly).map((p) => p.period);
  const labelW = phone ? 132 : 220;
  const th = { padding: '7px 10px', fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textAlign: 'right', whiteSpace: 'nowrap',
    borderBottom: '1px solid var(--border)', background: 'var(--surface)' };
  const sticky = { position: 'sticky', left: 0, zIndex: 1, background: 'var(--surface)', textAlign: 'left', minWidth: labelW, maxWidth: labelW,
    boxShadow: '1px 0 0 var(--border)' };
  return (
    // The grid scrolls sideways inside its card, never the page.
    <div style={{ ..._lgCard, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', fontSize: 12.5 }}>
        <thead>
          <tr>
            <th style={{ ...th, ...sticky }}>{grid.label}</th>
            {cols.map((c) => (c.kind === 'monthly' ? (
              <th key={c.period} style={th}>
                <button type="button" onClick={onToggleMonthly} aria-expanded="false"
                  style={{ ..._lgLink, fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  Monthly ({c.count}) {window.Icon && window.Icon.ChevronR ? <window.Icon.ChevronR size={11} /> : '›'}
                </button>
              </th>
            ) : (
              <th key={c.period} style={th}>
                {c.firstMonth ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <button type="button" onClick={onToggleMonthly} aria-expanded="true" title="Collapse months"
                      style={{ ..._lgLink, fontSize: 11, fontWeight: 600 }}>{window.Icon && window.Icon.ChevronL ? <window.Icon.ChevronL size={11} /> : '‹'}</button>
                    {c.label}
                  </span>
                ) : c.label}
              </th>
            )))}
          </tr>
        </thead>
        <tbody>
          {grid.rows.map((r) => (
            <tr key={r.id}>
              <th scope="row" style={{ ...sticky, padding: '5px 10px', borderTop: '1px solid var(--border)', verticalAlign: 'middle', fontWeight: 400 }}>
                <div style={{ fontSize: phone ? 13 : 12.5, color: r.sub ? 'var(--text-2)' : 'var(--text)', fontWeight: r.sub ? 400 : 600,
                  paddingLeft: r.sub ? 10 : 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.sub ? `${r.label} — ${r.sub}` : r.label}>
                  {r.sub || r.label}
                </div>
              </th>
              {cols.map((c) => {
                if (c.kind === 'monthly') {
                  const m = lgMonthlySummary(r, months);
                  return (
                    <td key={c.period} style={{ padding: '5px 10px', borderTop: '1px solid var(--border)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {m ? (
                        <button type="button" onClick={onToggleMonthly} title={(LG_STATUS[m.status] || [m.status])[0]}
                          style={{ ..._lgLink, display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-3)', fontSize: 12 }}>
                          <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 999, background: (LG_TONE[m.status] || LG_TONE.candidate).fg }} />
                          {m.n}
                          {m.flag && <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--amber-fg)' }} />}
                        </button>
                      ) : <span style={{ color: 'var(--text-4)' }}>—</span>}
                    </td>
                  );
                }
                return (
                  <td key={c.period} style={{ padding: '4px 8px', borderTop: '1px solid var(--border)', textAlign: 'right' }}>
                    <LgCell cell={r.cells[c.period]} onOpen={onOpen} phone={phone} />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── live job ───────────────────────────────────────────────────────────────

const LgLive = ({ live, canCancel, onCancel, phone }) => {
  const job = live.job || {};
  const scroller = React.useRef(null);
  React.useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [live.events.length]);
  return (
    <div style={{ ..._lgCard, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Reading the numbers</span>
        <LgPill map={LG_JOB} k={job.status || 'queued'} dot />
        {job.mode && <window.Pill hue="slate" sz="sm">{job.mode === 'incremental' ? 'Changes' : 'All'}</window.Pill>}
        <span style={{ flex: 1 }} />
        {canCancel && <window.Btn size="sm" variant="outline" onClick={onCancel}>Cancel</window.Btn>}
      </div>
      <div ref={scroller} role="log" aria-live="polite"
        style={{ maxHeight: phone ? 280 : 320, overflowY: 'auto', padding: '6px 0', fontSize: 12, lineHeight: 1.5 }}>
        {live.events.length === 0 && <div style={{ padding: '4px 14px', color: 'var(--text-4)' }}>Queued</div>}
        {live.events.map((ev) => {
          const [label, color] = LG_EVENT[ev.kind] || [ev.kind, 'var(--text-3)'];
          return (
            <div key={ev.id} style={{ display: 'grid', gridTemplateColumns: phone ? '72px 1fr' : '88px 52px 1fr', gap: 8, padding: '1px 14px' }}>
              <span style={{ color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{_lgClock(ev.at)}</span>
              {!phone && <span style={{ color, fontWeight: 500 }}>{label}</span>}
              <span style={{ color: 'var(--text-2)', overflowWrap: 'anywhere' }}>{ev.text}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── settings ───────────────────────────────────────────────────────────────

const LgSettingsForm = ({ L, canEdit, busy, onSave, phone, stacked = false }) => {
  const s = L.settings || {};
  const [draft, setDraft] = React.useState({ codename: s.codename || '', vertical: s.vertical || '', data_cutoff: s.data_cutoff || '' });
  React.useEffect(() => {
    setDraft({ codename: s.codename || '', vertical: s.vertical || '', data_cutoff: s.data_cutoff || '' });
  }, [s.codename, s.vertical, s.data_cutoff]);
  const patch = lgSettingsPatch(s, draft);
  const dirty = Object.keys(patch).length > 0;
  const field = (label, el) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <span style={_lgCap}>{label}</span>
      {el}
    </label>
  );
  const fs = phone ? { fontSize: 15, padding: '9px 10px' } : null;
  return (
    <div style={{ display: 'grid', gap: 10, alignItems: 'end', gridTemplateColumns: phone || stacked ? 'minmax(0, 1fr)' : 'minmax(0, 1.3fr) minmax(0, 1fr) minmax(0, 0.8fr) auto' }}>
      {field('Codename', (
        <input value={draft.codename} disabled={!canEdit} maxLength={80} onChange={(e) => setDraft((d) => ({ ...d, codename: e.target.value }))}
          style={{ ..._lgInput, ...fs }} aria-label="Codename" />
      ))}
      {field('Vertical', (
        <select value={draft.vertical} disabled={!canEdit} onChange={(e) => setDraft((d) => ({ ...d, vertical: e.target.value }))}
          style={{ ..._lgInput, ...fs }} aria-label="Vertical">
          <option value="">—</option>
          {(L.verticals || []).map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
        </select>
      ))}
      {field('Data cutoff', (
        <input type="date" value={draft.data_cutoff} disabled={!canEdit} onChange={(e) => setDraft((d) => ({ ...d, data_cutoff: e.target.value }))}
          style={{ ..._lgInput, ...fs }} aria-label="Data cutoff" />
      ))}
      {canEdit && (
        <div style={{ display: 'flex', justifyContent: phone || stacked ? 'flex-end' : 'flex-start' }}>
          <window.Btn size="sm" variant={dirty ? 'primary' : 'outline'} disabled={!dirty || busy} onClick={() => onSave(patch)}>Save</window.Btn>
        </div>
      )}
    </div>
  );
};

// ── fact drawer ────────────────────────────────────────────────────────────

const LgFactDrawer = ({ engagementId, factId, snapshot = null, canReview, onClose, onOpen, onChanged, phone }) => {
  const api = window.AtlasApi;
  const [d, setD] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [pending, setPending] = React.useState(null); // the action awaiting its reason
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const seq = React.useRef(0);
  const load = React.useCallback(() => {
    const my = ++seq.current;
    return api.cimLedgerFact(engagementId, factId, snapshot)
      .then((r) => { if (my === seq.current) { setD(r); setErr(null); } })
      .catch((e) => { if (my === seq.current) setErr(_lgErr(e).detail); });
  }, [engagementId, factId, snapshot]);
  React.useEffect(() => {
    setD(null); setErr(null); setPending(null); setReason('');
    load();
  }, [factId, snapshot]);

  const run = async (action, why) => {
    setBusy(true);
    try {
      await api.cimLedgerFactAction(engagementId, factId, action, why);
      window.pushToast && window.pushToast({ type: 'success', title: `${LG_DONE[action] || 'Saved'}: ${d ? d.fact.label : ''}` });
      setPending(null); setReason('');
      await load();
      onChanged && onChanged();
    } catch (e) {
      const x = _lgErr(e);
      window.pushToast && window.pushToast({ type: 'error', title: `Could not ${LG_ACTION_LABELS[action] ? LG_ACTION_LABELS[action].toLowerCase() : 'save'}`, body: x.detail });
      // 409 / 404: the ledger moved under the drawer — read it again, and the tab with it
      if (x.status === 409 || x.status === 404) {
        load();
        onChanged && onChanged();
      }
    } finally {
      setBusy(false);
    }
  };

  const f = d && d.fact;
  const title = f ? `${f.label}${f.dim_label ? ` — ${f.dim_label}` : ''}, ${f.period_label}` : 'Figure';
  const acts = canReview && d ? lgVisibleActions(d.actions) : [];
  const why = canReview && d ? lgApproveWhy(d.actions) : null;
  const asked = pending && acts.some((a) => a.key === pending) ? pending : null;
  const footer = acts.length ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
      {asked && (
        <>
          <LgReason value={reason} onChange={setReason} autoFocus phone={phone} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <window.Btn size="sm" variant="ghost" onClick={() => { setPending(null); setReason(''); }}>Back</window.Btn>
            <window.Btn size="sm" variant="primary" disabled={busy || !_lgReasonOk(reason)} onClick={() => run(asked, reason.trim())}>
              {LG_ACTION_LABELS[asked]}
            </window.Btn>
          </div>
        </>
      )}
      {!asked && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {acts.map((a) => (
            <window.Btn key={a.key} size="sm" variant={a.primary ? 'primary' : 'outline'} disabled={busy}
              onClick={() => (a.reason ? setPending(a.key) : run(a.key, null))}>{a.label}</window.Btn>
          ))}
        </div>
      )}
    </div>
  ) : null;

  return (
    <window.Drawer open onClose={onClose} title={title} subtitle={f ? f.ref : undefined} footer={footer} width={560} widthKey="cim-ledger-fact">
      {err && <div style={{ fontSize: 12.5, color: 'var(--rose-fg)' }}>{err}</div>}
      {!d && !err && <window.SkeletonRows rows={6} />}
      {f && (
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: phone ? 24 : 22, fontWeight: 650, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{f.display}</span>
            {f.exact && f.exact !== f.display && <span style={_lgMono}>{f.exact}</span>}
          </div>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 8 }}>
            {d.snapshot && (d.snapshot.in
              ? <window.Pill hue="violet" sz="sm">Locked v{d.snapshot.version}</window.Pill>
              : <window.Pill hue="slate" sz="sm">Current</window.Pill>)}
            <LgPill map={LG_STATUS} k={f.status} dot />
            {f.mgmt ? <window.Pill hue="teal" sz="sm">Per management</window.Pill> : <window.Pill hue="slate" sz="sm">{f.origin_label}</window.Pill>}
            <window.Pill hue="slate" sz="sm">{f.basis_label}</window.Pill>
            <window.Pill hue={f.confidence === 'low' ? 'rose' : f.confidence === 'medium' ? 'amber' : 'slate'} sz="sm">{_lgCap1(f.confidence)} confidence</window.Pill>
            {f.qualifier && <window.Pill hue="amber" sz="sm">{f.qualifier}</window.Pill>}
            {f.role === 'cross_check' && <window.Pill hue="slate" sz="sm">Cross-check</window.Pill>}
            {f.calculated && <window.Pill hue="blue" sz="sm">Calculated</window.Pill>}
          </div>
          {f.update_reason && (
            <div style={{ marginTop: 10, padding: '7px 10px', borderRadius: 7, background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: 12.5 }}>
              Newer data · {f.update_reason}
            </div>
          )}
          {why && (
            <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--text-2)' }}>{why}</div>
          )}

          <LgSection title="Source">
            {d.file && (
              <LgKV label="File">
                {d.file.name}
                {d.file.folder ? <span style={{ color: 'var(--text-4)' }}> · {d.file.folder}</span> : null}
                {' '}<window.Pill hue={d.file.ready ? 'green' : 'amber'} sz="sm">{d.file.ready ? 'Ready for CIM' : 'Not Ready'}</window.Pill>
                {!d.file.live && <> <window.Pill hue="rose" sz="sm">Not in the vault</window.Pill></>}
              </LgKV>
            )}
            {d.note && <LgKV label="Note">{d.note.label}</LgKV>}
            <LgKV label="Locator"><span style={_lgMono}>{f.locator}</span></LgKV>
            {f.as_written && <LgKV label="As written"><span style={_lgMono}>{f.as_written}</span></LgKV>}
            {f.status_reason && <LgKV label="Status">{f.status_reason}</LgKV>}
            {f.override_reason && <LgKV label="Override">{f.override_reason}</LgKV>}
            {f.approved_by && <LgKV label="Approved by">{f.approved_by}{f.approved_at ? ` · ${_lgWhen(f.approved_at)}` : ''}</LgKV>}
            {f.causes && f.causes.length > 0 && (
              <LgKV label="Needs">
                <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                  {f.causes.map((c) => <window.Pill key={c} hue="amber" sz="sm">{c}</window.Pill>)}
                </span>
              </LgKV>
            )}
            {f.flags && f.flags.length > 0 && (
              <LgKV label="Flags">
                <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                  {f.flags.map((x) => <window.Pill key={x} hue="amber" sz="sm">{x}</window.Pill>)}
                </span>
              </LgKV>
            )}
          </LgSection>

          {f.quote && (
            <LgSection title={f.mgmt ? 'Per management' : 'Quote'}>
              <blockquote style={{ margin: 0, padding: '8px 12px', borderLeft: '3px solid var(--teal-fg)', background: 'var(--surface-2)',
                borderRadius: 6, fontSize: phone ? 14 : 13, lineHeight: 1.55, color: 'var(--text)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                “{f.quote}”
              </blockquote>
            </LgSection>
          )}

          {d.aggregation && (
            <LgSection title="Export">
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12.5 }}>
                {Object.entries(d.aggregation).map(([k, v]) => (
                  <span key={k}><span style={{ color: 'var(--text-4)' }}>{LG_COUNT_LABELS[k] || k}</span> <b style={{ fontVariantNumeric: 'tabular-nums' }}>{v}</b></span>
                ))}
              </div>
            </LgSection>
          )}

          {d.inputs && d.inputs.length > 0 && (
            <LgSection title="Inputs">
              <div style={{ ..._lgCard, overflow: 'hidden' }}>
                {d.inputs.map((x) => <LgFactLine key={x.id} f={x} onOpen={onOpen} phone={phone} />)}
              </div>
            </LgSection>
          )}

          {d.others && d.others.length > 0 && (
            <LgSection title="Other readings">
              <div style={{ ..._lgCard, overflow: 'hidden' }}>
                {d.others.map((x) => (
                  <LgFactLine key={x.id} f={x} onOpen={onOpen} phone={phone}
                    extra={<window.Pill hue={x.agrees ? 'green' : 'rose'} sz="sm">{x.agrees ? 'Agrees' : 'Differs'}</window.Pill>} />
                ))}
              </div>
            </LgSection>
          )}

          {d.dcodes && d.dcodes.length > 0 && (
            <LgSection title="Conflicts">
              {d.dcodes.map((x) => (
                <div key={x.code} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '4px 0', fontSize: 12.5, flexWrap: 'wrap' }}>
                  <span style={{ ..._lgMono, minWidth: 48 }}>{x.code}</span>
                  <LgPill map={LG_DCODE} k={x.status} />
                  {x.holds && x.status === 'open' && <window.Pill hue="rose" sz="sm">Holds it</window.Pill>}
                  <span style={{ color: 'var(--text-3)' }}>{x.kind}{x.via ? ` · via ${x.via}` : ''}</span>
                  <span style={{ color: 'var(--text)', flex: 1, minWidth: 160, overflowWrap: 'anywhere' }}>{x.title}</span>
                </div>
              ))}
            </LgSection>
          )}

          {d.events && d.events.length > 0 && (
            <LgSection title="History">
              {d.events.map((e) => (
                <div key={e.id} style={{ display: 'grid', gridTemplateColumns: phone ? '1fr' : '120px minmax(0, 1fr)', gap: phone ? 0 : 8, padding: '4px 0', fontSize: 12, borderTop: '1px solid var(--border)' }}>
                  <span style={{ color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{_lgWhen(e.at)}</span>
                  <span style={{ color: 'var(--text-2)', overflowWrap: 'anywhere' }}>
                    <b style={{ fontWeight: 600, color: 'var(--text)' }}>{LG_EVENT_ACTION[e.action] || _lgCap1(e.action)}</b>
                    {' · '}{e.who}{e.dcode ? ` · ${e.dcode}` : ''}{e.reason ? ` · ${e.reason}` : ''}
                  </span>
                </div>
              ))}
            </LgSection>
          )}
        </div>
      )}
    </window.Drawer>
  );
};

// ── discrepancies ──────────────────────────────────────────────────────────

const LgDiscrepancy = ({ d, canReview, busy, onResolve, onReopen, onOpen, phone }) => {
  const [choice, setPick] = React.useState(d.options && d.options.length === 1 ? d.options[0].id : null);
  // only a candidate the D-code still holds (a re-run keeps the code but may change its candidates)
  const pick = (d.options || []).some((o) => o.id === choice) ? choice : null;
  const [reason, setReason] = React.useState('');
  const [all, setAll] = React.useState(false);
  const [reopening, setReopening] = React.useState(false);
  const held = d.held || [];
  const shown = all ? held : held.slice(0, LG_HELD_PREVIEW);
  const ok = _lgReasonOk(reason);
  return (
    <div style={{ ..._lgCard, overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span title={`Conflict ${d.code}`} style={{ ..._lgMono, color: 'var(--text-4)' }}>{d.code}</span>
        <window.Pill hue={LG_KIND_HUE[d.kind] || 'slate'} sz="sm">{d.kind_label}</window.Pill>
        {!d.open && <LgPill map={LG_DCODE} k={d.status} />}
        {d.check && <span style={_lgMono}>{d.check}</span>}
        <span style={{ fontSize: phone ? 14 : 13, color: 'var(--text)', fontWeight: 500, flex: 1, minWidth: 180, overflowWrap: 'anywhere' }}>{d.title}</span>
      </div>
      {(d.lhs || d.rhs || d.difference || (d.differs_by && d.differs_by.length > 0)) && (
        <div style={{ padding: '0 14px 8px', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', fontSize: 12.5 }}>
          {(d.lhs || d.rhs) && (
            <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-2)' }}>{d.lhs || '—'} <span style={{ color: 'var(--text-4)' }}>vs</span> {d.rhs || '—'}</span>
          )}
          {d.difference && <window.Pill hue="amber" sz="sm">Differs by {d.difference}</window.Pill>}
          {(d.differs_by || []).map((x) => <window.Pill key={x} hue="slate" sz="sm">{x}</window.Pill>)}
        </div>
      )}
      {held.length > 0 && (
        <div>
          {shown.map((h) => (
            <LgFactLine key={`${h.id}:${h.hold_role}`} f={h} onOpen={onOpen} phone={phone}
              extra={(
                <>
                  {h.side && <window.Pill hue={h.side === 'statement' ? 'blue' : 'slate'} sz="sm">{h.side === 'statement' ? 'Statement' : 'Input'}</window.Pill>}
                  {h.via && <window.Pill hue="violet" sz="sm">via {h.via}</window.Pill>}
                  {d.picked_fact_id === h.id && <window.Pill hue="green" sz="sm">Picked</window.Pill>}
                </>
              )} />
          ))}
          {held.length > LG_HELD_PREVIEW && (
            <div style={{ borderTop: '1px solid var(--border)', padding: '6px 14px' }}>
              <button type="button" style={{ ..._lgLink, fontSize: 12 }} onClick={() => setAll((x) => !x)}>
                {all ? 'Fewer' : `${held.length - LG_HELD_PREVIEW} more`}
              </button>
            </div>
          )}
        </div>
      )}
      {!d.open && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '8px 14px', fontSize: 12.5, color: 'var(--text-2)', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {d.picked_side && <window.Pill hue="green" sz="sm">{d.picked_side === 'statement' ? 'Statement' : 'Input'}</window.Pill>}
          <span style={{ flex: 1, minWidth: 160, overflowWrap: 'anywhere' }}>
            {[d.reason, d.resolved_by, _lgWhen(d.resolved_at)].filter(Boolean).join(' · ')}
          </span>
          {canReview && !reopening && <window.Btn size="sm" variant="outline" onClick={() => setReopening(true)}>Reopen</window.Btn>}
        </div>
      )}
      {canReview && !d.open && reopening && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <LgReason value={reason} onChange={setReason} autoFocus phone={phone} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <window.Btn size="sm" variant="ghost" onClick={() => { setReopening(false); setReason(''); }}>Back</window.Btn>
            <window.Btn size="sm" variant="primary" disabled={busy || !ok} onClick={() => onReopen(d.code, reason.trim())}>Reopen</window.Btn>
          </div>
        </div>
      )}
      {canReview && d.open && d.resolve && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--surface-2)' }}>
          {d.resolve === 'pick' && (
            (d.options || []).length ? (
              <div role="radiogroup" aria-label="Pick" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {d.options.map((o) => (
                  <label key={o.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: phone ? 14 : 12.5, cursor: 'pointer',
                    padding: phone ? '4px 0' : 0, flexWrap: phone ? 'wrap' : 'nowrap' }}>
                    <input type="radio" name={`pick-${d.code}`} checked={pick === o.id} onChange={() => setPick(o.id)} style={{ marginTop: 3 }} />
                    <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, minWidth: 90 }}>{o.display}</span>
                    <span style={{ color: 'var(--text-3)', overflowWrap: 'anywhere', minWidth: 0, flex: phone ? '1 1 100%' : '0 1 auto',
                      paddingLeft: phone ? 22 : 0 }}>{[o.source, o.locator].filter(Boolean).join(' · ')}</span>
                    {o.status === 'approved' || o.status === 'overridden' ? <window.Pill hue="green" sz="sm">Approved</window.Pill> : null}
                  </label>
                ))}
              </div>
            ) : <span style={{ fontSize: 12.5, color: 'var(--text-4)' }}>No other reading</span>
          )}
          <LgReason value={reason} onChange={setReason} phone={phone} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {d.resolve === 'pick' && (
              <window.Btn size="sm" variant="primary" disabled={busy || !ok || !pick}
                onClick={() => onResolve(d.code, { action: 'pick', fact_id: pick, reason: reason.trim() })}>Resolve</window.Btn>
            )}
            {d.resolve === 'sides' && (
              <>
                <window.Btn size="sm" variant="outline" disabled={busy || !ok}
                  onClick={() => onResolve(d.code, { action: 'pick', side: 'input', reason: reason.trim() })}>Input</window.Btn>
                <window.Btn size="sm" variant="primary" disabled={busy || !ok}
                  onClick={() => onResolve(d.code, { action: 'pick', side: 'statement', reason: reason.trim() })}>Statement</window.Btn>
              </>
            )}
            {d.resolve === 'accept' && (
              <window.Btn size="sm" variant="primary" disabled={busy || !ok}
                onClick={() => onResolve(d.code, { action: 'accept', reason: reason.trim() })}>Accept as stated</window.Btn>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const LgDiscrepancies = ({ dq, err, onRetry, canReview, busy, onResolve, onResolveMany, onReopen, onOpen, phone }) => {
  const [showResolved, setShowResolved] = React.useState(false);
  const [many, setMany] = React.useState(null); // {check, codes}
  const [reason, setReason] = React.useState('');
  if (!dq && err) {
    return (
      <div style={{ ..._lgCard, padding: '18px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 12.5, color: 'var(--rose-fg)', textAlign: 'center', overflowWrap: 'anywhere' }}>{err}</div>
        <window.Btn size="sm" variant="outline" onClick={onRetry}>Try again</window.Btn>
      </div>
    );
  }
  if (!dq) return <window.SkeletonRows rows={5} />;
  const open = dq.items.filter((d) => d.open);
  const done = dq.items.filter((d) => !d.open);
  const groups = canReview ? lgReconcileGroups(dq.items) : [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {err && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5, color: 'var(--rose-fg)' }}>
          <span style={{ overflowWrap: 'anywhere' }}>{err}</span>
          <window.Btn size="sm" variant="outline" onClick={onRetry}>Try again</window.Btn>
        </div>
      )}
      {groups.map((g) => (
        <div key={g.check} style={{ ..._lgCard, padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ ..._lgMono, fontWeight: 600 }}>{g.check}</span>
            <span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{_lgPlural(g.codes.length, 'reconcile conflict')}</span>
            <span style={{ flex: 1 }} />
            {(!many || many.check !== g.check) && (
              <window.Btn size="sm" variant="outline" onClick={() => { setMany(g); setReason(''); }}>Pick statement for all shown</window.Btn>
            )}
          </div>
          {many && many.check === g.check && (
            <>
              <LgReason value={reason} onChange={setReason} autoFocus phone={phone} />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <window.Btn size="sm" variant="ghost" onClick={() => setMany(null)}>Back</window.Btn>
                <window.Btn size="sm" variant="primary" disabled={busy || !_lgReasonOk(reason)}
                  onClick={() => onResolveMany(g.codes, reason.trim()).then((ok) => { if (ok) setMany(null); })}>
                  Statement for {g.codes.length}
                </window.Btn>
              </div>
            </>
          )}
        </div>
      ))}
      {open.length === 0 && <LgEmpty>No open conflicts</LgEmpty>}
      {open.map((d) => (
        <LgDiscrepancy key={`${d.code}:${d.status}`} d={d} canReview={canReview} busy={busy} onResolve={onResolve} onReopen={onReopen} onOpen={onOpen} phone={phone} />
      ))}
      {done.length > 0 && (
        <div>
          <button type="button" onClick={() => setShowResolved((x) => !x)} aria-expanded={showResolved}
            style={{ ..._lgLink, fontSize: 12.5, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 0' }}>
            Resolved ({done.length}) {showResolved ? '▾' : '▸'}
          </button>
          {showResolved && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 6 }}>
              {done.map((d) => (
                <LgDiscrepancy key={`${d.code}:${d.status}`} d={d} canReview={canReview} busy={busy} onResolve={onResolve} onReopen={onReopen} onOpen={onOpen} phone={phone} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── freeze ─────────────────────────────────────────────────────────────────

const LgFreezeDrawer = ({ engagementId, onClose, onFrozen, phone }) => {
  const api = window.AtlasApi;
  const [plan, setPlan] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [ack, setAck] = React.useState(false);
  const [qoe, setQoe] = React.useState('');
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [qoeErr, setQoeErr] = React.useState(null); // a 409's QoE block {message, value}: holds while that choice stands
  React.useEffect(() => {
    api.cimLedgerFreezePlan(engagementId)
      .then((p) => { setPlan(p); setQoe((p && p.qoe && p.qoe.suggested && p.qoe.suggested[0]) || ''); })
      .catch((e) => setErr(_lgErr(e).detail));
  }, [engagementId]);
  const warnings = (plan && plan.warnings) || [];
  const blocks = (plan && plan.blocks) || [];
  const needQoe = !!(plan && plan.qoe && plan.qoe.required);
  const qoeBad = qoeErr && qoeErr.value === qoe ? qoeErr.message : null;
  const can = plan && !blocks.length && (!warnings.length || ack) && (!needQoe || qoe) && !qoeBad && !busy;
  const freeze = async () => {
    setBusy(true);
    try {
      const r = await api.cimLedgerFreeze(engagementId, {
        acknowledge: ack ? (plan.acknowledge || []) : [], qoe_attachment_id: qoe || null, note: note.trim() || null,
      });
      window.pushToast && window.pushToast({ type: 'success', title: `Numbers locked as v${r.version}` });
      onFrozen && onFrozen(r);
    } catch (e) {
      const x = _lgErr(e);
      if (x.status === 409 && x.body) {
        const all = (x.body.blocks || []).map((b) => ({ kind: b.kind, message: b.message, codes: b.codes }));
        const isQoe = (b) => /^qoe_/.test(b.kind || '');
        const qm = all.filter(isQoe).map((b) => b.message).join(' · ');
        setQoeErr(qm ? { message: qm, value: qoe } : null);
        setPlan((p) => ({ ...p, blocks: all.filter((b) => !isQoe(b)),
          warnings: (x.body.warnings || []).map((w) => ({ kind: w.kind, message: w.message, codes: w.codes })),
          acknowledge: x.body.acknowledge || (p && p.acknowledge) || [] }));
        setAck(false);
        // the ledger moved under the dialog: the QoE choice (files, required) and the counts are the plan's now
        api.cimLedgerFreezePlan(engagementId)
          .then((p2) => {
            if (!p2) return;
            setPlan((p) => ({ ...p, counts: p2.counts || p.counts, qoe: p2.qoe || p.qoe }));
            // a file the plan no longer lists can't stay chosen
            const ids = ((p2.qoe && p2.qoe.files) || []).map((f) => f.id);
            setQoe((cur) => (cur && !ids.includes(cur) ? '' : cur));
          })
          .catch(() => {});
      } else {
        window.pushToast && window.pushToast({ type: 'error', title: 'Could not lock the numbers', body: x.detail });
      }
    } finally {
      setBusy(false);
    }
  };
  const footer = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
      <window.Btn size="sm" variant="ghost" onClick={onClose}>Close</window.Btn>
      <window.Btn size="sm" variant="primary" disabled={!can} onClick={freeze}>Lock</window.Btn>
    </div>
  );
  return (
    <window.Drawer open onClose={onClose} title="Lock numbers" footer={footer} width={520} widthKey="cim-ledger-freeze">
      {err && <div style={{ fontSize: 12.5, color: 'var(--rose-fg)' }}>{err}</div>}
      {!plan && !err && <window.SkeletonRows rows={5} />}
      {plan && (
        <div>
          <div style={{ display: 'grid', gap: 8, gridTemplateColumns: phone ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))' }}>
            {LG_FREEZE_COUNTS.map(([k, label]) => (
              <div key={k} style={{ ..._lgCard, padding: '8px 10px' }}>
                <div style={_lgCap}>{label}</div>
                <div style={{ fontSize: 18, fontWeight: 650, fontVariantNumeric: 'tabular-nums', color: 'var(--text)' }}>{plan.counts[k] || 0}</div>
              </div>
            ))}
          </div>
          {blocks.length > 0 && (
            <LgSection title="Blocks">
              {blocks.map((b, i) => (
                <div key={`${b.kind}:${i}`} style={{ padding: '7px 10px', borderRadius: 7, background: 'var(--rose-bg)', color: 'var(--rose-fg)', fontSize: 12.5, marginBottom: 6, overflowWrap: 'anywhere' }}>
                  {b.message}
                </div>
              ))}
            </LgSection>
          )}
          {warnings.length > 0 && (
            <LgSection title="Warnings">
              {warnings.map((w, i) => (
                <div key={`${w.kind}:${i}`} style={{ padding: '7px 10px', borderRadius: 7, background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: 12.5, marginBottom: 6, overflowWrap: 'anywhere' }}>
                  {w.message}{w.codes && w.codes.length ? ` · ${w.codes.join(', ')}` : ''}
                </div>
              ))}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: phone ? 14 : 12.5, marginTop: 4, cursor: 'pointer' }}>
                <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
                I acknowledge
              </label>
            </LgSection>
          )}
          {plan.qoe && (plan.qoe.show || qoeErr) && (
            <LgSection title="QoE report">
              <select value={qoe} onChange={(e) => { setQoe(e.target.value); setQoeErr(null); }} aria-label="QoE report"
                aria-invalid={qoeBad ? true : undefined} style={{ ..._lgInput, fontSize: phone ? 15 : 12.5, borderColor: qoeBad ? 'var(--rose-fg)' : 'var(--border)' }}>
                <option value="">{needQoe ? 'Choose a file' : 'None'}</option>
                {(plan.qoe.files || []).map((f) => <option key={f.id} value={f.id}>{f.name}{f.folder ? ` · ${f.folder}` : ''}</option>)}
              </select>
              {qoeBad && <div role="alert" style={{ fontSize: 12, color: 'var(--rose-fg)', marginTop: 4, overflowWrap: 'anywhere' }}>{qoeBad}</div>}
            </LgSection>
          )}
          <LgSection title="Note">
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500} aria-label="Note"
              style={{ ..._lgInput, resize: 'vertical', fontSize: phone ? 15 : 12.5 }} />
          </LgSection>
        </div>
      )}
    </window.Drawer>
  );
};

// A reason, then one action (Override not-Ready, Use new ranking).
const LgReasonDrawer = ({ title, confirmLabel, busy, onConfirm, onClose, phone }) => {
  const [reason, setReason] = React.useState('');
  const footer = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
      <window.Btn size="sm" variant="ghost" onClick={onClose}>Close</window.Btn>
      <window.Btn size="sm" variant="primary" disabled={busy || !_lgReasonOk(reason)} onClick={() => onConfirm(reason.trim())}>{confirmLabel}</window.Btn>
    </div>
  );
  return (
    <window.Drawer open onClose={onClose} title={title} footer={footer} width={440} widthKey="cim-ledger-reason">
      <LgReason value={reason} onChange={setReason} autoFocus phone={phone} />
    </window.Drawer>
  );
};

// ── the tab ────────────────────────────────────────────────────────────────

const CimLedgerTab = ({ engagementId, phone = false, onJobChange, initialSub = 'statements' }) => {
  const { useState, useEffect, useRef, useCallback } = React;
  const api = window.AtlasApi;
  const toast = (t) => window.pushToast && window.pushToast(t);
  const [L, setL] = useState(null);
  const [err, setErr] = useState(null);
  const [sub, setSub] = useState(initialSub === 'discrepancies' ? 'discrepancies' : 'statements');
  const [stmt, setStmt] = useState(null);
  const [snapV, setSnapV] = useState(null);
  const [grid, setGrid] = useState(null);
  const [gridErr, setGridErr] = useState(null);
  const [monthly, setMonthly] = useState(false);
  const [factId, setFactId] = useState(null);
  const [dq, setDq] = useState(null);
  const [dqErr, setDqErr] = useState(null);
  const [dqNonce, setDqNonce] = useState(0);         // bumped when a job ends: the queue reads again
  const [freezing, setFreezing] = useState(false);
  const [settings, setSettings] = useState(false);
  const [reasonFor, setReasonFor] = useState(null); // 'override_not_ready' | 'use_ranking'
  const [live, setLive] = useState(null);           // {jobId, job, events}
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState(false);
  const [why, setWhy] = useState(null);              // 'ledger' | 'snapshot' — the update popover
  const liveRef = useRef(live);
  liveRef.current = live;
  const finished = useRef(new Set());
  const seq = useRef({ L: 0, grid: 0, dq: 0 });
  const moreRef = useRef(null);
  const menuRef = useRef(null);
  // What is selected now: a reload that lands after an await reads this, never the click's render.
  const sel = useRef({ stmt, snapV, sub });
  sel.current = { stmt, snapV, sub };
  // Never act on, or show, a grid of another statement or snapshot than the one selected.
  const gridFor = lgGridFor(grid, stmt, snapV);

  const loadL = useCallback(() => {
    const my = ++seq.current.L;
    return api.cimLedger(engagementId)
      .then((r) => { if (my === seq.current.L) { setL(r); setErr(null); } return r; })
      .catch((e) => { if (my === seq.current.L) setErr(_lgErr(e)); return null; });
  }, [engagementId]);
  const loadGrid = useCallback((statement, v) => {
    if (!statement) return Promise.resolve(null);
    const my = ++seq.current.grid;
    return api.cimLedgerGrid(engagementId, statement, v)
      .then((g) => { if (my === seq.current.grid) { setGrid(g); setGridErr(null); } return g; })
      .catch((e) => { if (my === seq.current.grid) setGridErr(_lgErr(e).detail); return null; });
  }, [engagementId]);
  const loadDq = useCallback(() => {
    const my = ++seq.current.dq;
    setDqErr(null);
    return api.cimLedgerDiscrepancies(engagementId)
      .then((r) => { if (my === seq.current.dq) setDq(r); return r; })
      .catch((e) => { if (my === seq.current.dq) setDqErr(_lgErr(e).detail); return null; });
  }, [engagementId]);

  useEffect(() => {
    setL(null); setErr(null); setGrid(null); setDq(null); setDqErr(null); setStmt(null); setSnapV(null); setLive(null); setFactId(null);
    finished.current = new Set();
    loadL();
  }, [engagementId]);

  // The More menu: Escape closes it (focus back on its button); opening focuses the first item.
  window.useEscapeKey(menu, () => { setMenu(false); if (moreRef.current) moreRef.current.focus(); });
  useEffect(() => {
    const first = menu && menuRef.current && menuRef.current.querySelector('[role="menuitem"]:not([disabled])');
    if (first) first.focus();
  }, [menu]);

  // A job in flight (started here, by another tab or by another person) → the live panel.
  useEffect(() => {
    if (!L || !L.active_job) return;
    const j = L.active_job;
    if (finished.current.has(j.id)) return;
    setLive((cur) => (cur && cur.jobId === j.id ? cur : { jobId: j.id, job: j, events: [] }));
  }, [L]);
  useEffect(() => { if (onJobChange) onJobChange(!!live); }, [!!live]);

  // The first statement with figures (or the one already chosen, if it still has some).
  const statements = ((gridFor && gridFor.statements) || (L && L.statements) || []).filter((s) => s.figures > 0);
  useEffect(() => {
    if (!L || !L.has_ledger) return;
    const list = (L.statements || []).filter((s) => s.figures > 0);
    setStmt((cur) => (cur && list.some((s) => s.key === cur) ? cur : (list[0] && list[0].key) || null));
  }, [L]);
  // Another statement or snapshot: drop the previous grid (and a reason dialog opened on it).
  useEffect(() => {
    setGrid(null); setGridErr(null); setReasonFor(null);
    if (stmt) loadGrid(stmt, snapV);
  }, [stmt, snapV]);
  useEffect(() => { if (sub === 'discrepancies' && L && L.has_ledger) loadDq(); }, [sub, L && L.has_ledger, dqNonce]);

  const refresh = useCallback(() => {
    const s = sel.current;
    return Promise.all([loadL(), s.stmt ? loadGrid(s.stmt, s.snapV) : null, s.sub === 'discrepancies' ? loadDq() : null]);
  }, [loadL, loadGrid, loadDq]);

  const finish = useCallback((job) => {
    if (!job || finished.current.has(job.id)) return;
    finished.current.add(job.id);
    setLive((cur) => (cur && cur.jobId === job.id ? null : cur));
    if (job.status === 'completed') toast({ type: 'success', title: 'Numbers read' });
    else if (job.status === 'failed') toast({ type: 'error', title: 'Reading the numbers failed', body: job.error || undefined });
    else toast({ type: 'info', title: 'Reading the numbers cancelled' });
    loadL().then((r) => {
      const list = ((r && r.statements) || []).filter((s) => s.figures > 0);
      const cur = sel.current.stmt;
      const next = cur && list.some((s) => s.key === cur) ? cur : (list[0] && list[0].key) || null;
      if (next) loadGrid(next, null);
    });
    setSnapV(null);
    setDq(null);
    setDqNonce((n) => n + 1);
  }, [loadL, loadGrid]);

  // Progress lines every 5 s while a job runs (paused while the tab is hidden).
  window.usePolling(async () => {
    const cur = liveRef.current;
    if (!cur) return 'idle';
    const last = cur.events.length ? cur.events[cur.events.length - 1].id : 0;
    const r = await api.cimLedgerJob(engagementId, cur.jobId, { after: last, eventsOnly: true });
    const now = liveRef.current;
    if (!now || now.jobId !== cur.jobId) return 'gone';
    const evs = (r && r.events) || [];
    setLive((c) => (c && c.jobId === cur.jobId ? { ...c, job: { ...c.job, status: r.status, error: r.error }, events: lgMergeEvents(c.events, evs) } : c));
    if (r && LG_TERMINAL.has(r.status)) finish({ ...cur.job, id: cur.jobId, status: r.status, error: r.error });
    return `${r ? r.status : ''}:${evs.length ? evs[evs.length - 1].id : last}`;
  }, {
    active: !!live, key: live ? live.jobId : null, first: 5000, stepAfter: 60 * 60 * 1000,
    giveUpAfter: 0, immediate: true, signature: (s) => s,
  });

  const extract = async (mode) => {
    setMenu(false);
    setBusy(true);
    try {
      const r = await api.cimLedgerExtract(engagementId, mode);
      setLive({ jobId: r.job_id, job: { id: r.job_id, status: r.status, mode }, events: [] });
      setSub('statements');
    } catch (e) {
      toast({ type: 'error', title: 'Could not start reading the numbers', body: _lgErr(e).detail });
      loadL();
    } finally {
      setBusy(false);
    }
  };
  const cancel = async () => {
    const cur = liveRef.current;
    if (!cur) return;
    const ok = window.showConfirm ? await window.showConfirm({ title: 'Stop reading the numbers?', confirmLabel: 'Stop reading', cancelLabel: 'Keep running', danger: true }) : true;
    if (!ok) return;
    try {
      const j = await api.cimLedgerCancel(engagementId, cur.jobId);
      finish({ ...cur.job, ...j, id: cur.jobId });
    } catch (e) {
      toast({ type: 'error', title: 'Could not cancel', body: _lgErr(e).detail });
      loadL();
    }
  };
  const saveSettings = async (patch) => {
    setBusy(true);
    try {
      await api.cimLedgerSettings(engagementId, patch);
      toast({ type: 'success', title: 'Ledger settings saved' });
      setSettings(false);
      await loadL();
    } catch (e) {
      toast({ type: 'error', title: 'Could not save', body: _lgErr(e).detail });
    } finally {
      setBusy(false);
    }
  };
  // One statement: its clean figures, or (after a confirm) the ones per management.
  const approveView = async (scope) => {
    const g = gridFor;
    const ids = g ? (scope === 'per_management' ? g.per_management_ids : g.clean_ids) || [] : [];
    if (!ids.length) return;
    if (scope === 'per_management') {
      const ok = window.showConfirm ? await window.showConfirm({
        title: `Approve ${_lgPlural(ids.length, 'figure')} per management?`, body: `${g.label}: not read from a document`,
        confirmLabel: 'Approve', cancelLabel: 'Not now',
      }) : true;
      if (!ok) return;
    }
    setBusy(true);
    try {
      const r = await api.cimLedgerBulk(engagementId, 'approve', ids, null, scope);
      toast({ type: 'success', title: lgBulkSummary(r), body: lgSkipDetail(r) || undefined });
      await refresh(); // busy until the grid it acts on is the new one
    } catch (e) {
      toast({ type: 'error', title: 'Could not approve', body: _lgErr(e).detail });
    } finally {
      setBusy(false);
    }
  };
  // Every statement at once: the ids are read on the click, confirmed with their counts.
  const approveClean = async () => {
    setBusy(true);
    let c = null;
    try {
      c = await api.cimLedgerClean(engagementId);
    } catch (e) {
      toast({ type: 'error', title: 'Could not read the clean figures', body: _lgErr(e).detail });
      setBusy(false);
      return;
    }
    const ids = (c && c.ids) || [];
    if (!ids.length) {
      toast({ type: 'info', title: 'Nothing clean to approve' });
      await refresh();
      setBusy(false);
      return;
    }
    setBusy(false);
    const ok = window.showConfirm ? await window.showConfirm({
      title: `Approve ${ids.length.toLocaleString()} clean figure${ids.length === 1 ? '' : 's'}?`, body: lgCleanLine(c) || undefined,
      confirmLabel: 'Approve', cancelLabel: 'Not now',
    }) : true;
    if (!ok) return;
    setBusy(true);
    const done = [];
    try {
      for (const chunk of lgChunks(ids)) done.push(await api.cimLedgerBulk(engagementId, 'approve', chunk, null, 'clean'));
      const r = lgMergeBulk(done);
      toast({ type: 'success', title: lgBulkSummary(r), body: lgSkipDetail(r) || undefined });
    } catch (e) {
      const r = lgMergeBulk(done);
      toast({ type: 'error', title: 'Could not approve', body: [done.length ? lgBulkSummary(r) : null, _lgErr(e).detail].filter(Boolean).join(' · ') });
    } finally {
      await refresh();
      setBusy(false);
    }
  };
  const confirmReason = async (reason) => {
    const g = gridFor;
    if (!g) { setReasonFor(null); return; }
    setBusy(true);
    try {
      if (reasonFor === 'override_not_ready') {
        const r = await api.cimLedgerBulk(engagementId, 'override_not_ready', g.not_ready_ids, reason);
        toast({ type: 'success', title: lgBulkSummary(r), body: lgSkipDetail(r) || undefined });
      } else if (reasonFor === 'use_ranking' && g.ranking) {
        const r = await api.cimLedgerUseRanking(engagementId, g.ranking.latest, reason);
        toast({ type: 'success', title: `Ranking ${r.alias_set} in use`, body: r.rejected ? `${_lgPlural(r.rejected, 'figure')} rejected` : undefined });
      }
      setReasonFor(null);
      await refresh();
    } catch (e) {
      refused(e, 'Could not save');
      const st = _lgErr(e).status;
      if (st === 409 || st === 404) setReasonFor(null);
    } finally {
      setBusy(false);
    }
  };
  // A refused D-code action (409 / 404): the ledger moved — read it again so a retry isn't the same refusal.
  const refused = (e, title) => {
    const x = _lgErr(e);
    toast({ type: 'error', title, body: x.detail });
    if (x.status === 409 || x.status === 404) refresh();
  };
  const resolve = async (code, body) => {
    setBusy(true);
    try {
      const r = await api.cimLedgerResolve(engagementId, code, body);
      toast({ type: 'success', title: `${code} ${r.status}` });
      refresh();
      return true;
    } catch (e) {
      refused(e, `Could not resolve ${code}`);
      return false;
    } finally {
      setBusy(false);
    }
  };
  const resolveMany = async (codes, reason) => {
    setBusy(true);
    try {
      const r = await api.cimLedgerResolveMany(engagementId, codes, reason);
      toast({ type: 'success', title: `${_lgPlural(r.resolved || 0, 'conflict')} settled` });
      refresh();
      return true;
    } catch (e) {
      refused(e, 'Could not resolve');
      return false;
    } finally {
      setBusy(false);
    }
  };
  const reopenD = async (code, reason) => {
    setBusy(true);
    try {
      await api.cimLedgerReopenDcode(engagementId, code, reason);
      toast({ type: 'success', title: `${code} reopened` });
      refresh();
    } catch (e) {
      refused(e, `Could not reopen ${code}`);
    } finally {
      setBusy(false);
    }
  };

  if (err && !L) {
    return (
      <div style={{ ..._lgCard, padding: '24px 18px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)' }}>{err.detail}</div>
        {err.status !== 404 && err.status !== 422 && <window.Btn size="sm" variant="outline" onClick={() => loadL()}>Try again</window.Btn>}
      </div>
    );
  }
  if (!L) return <window.SkeletonRows rows={8} />;

  const gate = lgExtractGate(L);
  const failed = !live && L.last_job && (L.last_job.status === 'failed' || L.last_job.status === 'cancelled') ? L.last_job : null;
  const upd = L.update_available || {};
  const snapUpd = L.snapshot_update && L.snapshots && L.snapshots.length ? L.snapshot_update : null;
  const frozenView = snapV != null;
  const canReview = !!L.can_review && !frozenView;
  const dOpen = (L.dcodes && L.dcodes.open) || 0;
  const counts = L.facts || {};

  const extractBtn = (mode, label, primary) => (
    <window.Btn size="sm" variant={primary ? 'primary' : 'outline'} disabled={!gate.ok || busy || !!live} title={gate.why || undefined}
      icon={window.Icon && window.Icon.Play ? <window.Icon.Play size={12} /> : null} onClick={() => extract(mode)}>{label}</window.Btn>
  );
  const gateLabel = !gate.ok && gate.why ? <span style={{ fontSize: 12, color: 'var(--text-4)' }}>{gate.why}</span> : null;

  const failedBanner = failed && (
    <div style={{ ..._lgCard, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      borderColor: failed.status === 'failed' ? 'var(--rose-fg)' : 'var(--border)' }}>
      <LgPill map={LG_JOB} k={failed.status} dot />
      <span style={{ fontSize: 12.5, color: 'var(--text-2)', flex: 1, minWidth: 160, overflowWrap: 'anywhere' }}>
        {failed.error || _lgWhen(failed.finished_at)}
      </span>
      {gate.ok && extractBtn(failed.mode === 'incremental' && L.has_map ? 'incremental' : 'full', 'Read again', true)}
    </div>
  );

  // No ledger yet: the settings and Extract, or the first extraction's progress.
  if (!L.has_ledger) {
    const setup = (
      <div style={{ ..._lgCard, padding: phone ? '12px 14px' : '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <LgSettingsForm L={L} canEdit={!!L.can_review} busy={busy} onSave={saveSettings} phone={phone} />
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', fontSize: 12.5 }}>
          {[['ready', 'Ready'], ['readable', 'Readable'], ['not_read', 'Not read']].map(([k, label]) => (
            <span key={k}><span style={{ color: 'var(--text-4)' }}>{label}</span> <b style={{ fontVariantNumeric: 'tabular-nums' }}>{(L.vault || {})[k] || 0}</b></span>
          ))}
          <span style={{ flex: 1 }} />
          {gateLabel}
          {extractBtn('full', 'Read the numbers', true)}
        </div>
      </div>
    );
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {failedBanner}
        {live ? <LgLive live={live} canCancel={!!L.can_run} onCancel={cancel} phone={phone} /> : setup}
      </div>
    );
  }

  const snapshots = L.snapshots || [];
  const dv = window.AtlasDevice || { phone, touch: phone };
  const onMenuKey = (e) => {
    if (e.key === 'Tab') { setMenu(false); return; } // focus moves on; the menu goes with it
    const items = menuRef.current ? Array.from(menuRef.current.querySelectorAll('[role="menuitem"]:not([disabled])')) : [];
    const next = lgMenuStep(items.length, items.indexOf(document.activeElement), e.key);
    if (next == null) return;
    e.preventDefault();
    items[next].focus();
  };
  const topBar = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <select value={snapV == null ? '' : String(snapV)} onChange={(e) => setSnapV(e.target.value === '' ? null : Number(e.target.value))}
        aria-label="Locked numbers" style={{ ..._lgInput, width: 'auto', maxWidth: phone ? '100%' : 280, fontSize: phone ? 14 : 12.5 }}>
        <option value="">Current</option>
        {snapshots.map((s) => <option key={s.id} value={s.version}>{lgSnapshotLabel(s)}</option>)}
      </select>
      {snapUpd && snapUpd.update_available && (
        <button type="button" onClick={() => setWhy(why === 'snapshot' ? null : 'snapshot')} aria-expanded={why === 'snapshot'}
          style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer' }}>
          <window.Pill hue="amber" sz="sm" dot>Changed since locked</window.Pill>
        </button>
      )}
      {upd.available && (
        <button type="button" onClick={() => setWhy(why === 'ledger' ? null : 'ledger')} aria-expanded={why === 'ledger'}
          style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer' }}>
          <window.Pill hue="amber" sz="sm" dot>Newer data</window.Pill>
        </button>
      )}
      <span style={{ flex: 1 }} />
      {gateLabel}
      {L.has_map ? extractBtn('incremental', 'Read changes', !!upd.available) : extractBtn('full', 'Read the numbers', true)}
      <div style={{ position: 'relative' }}>
        {/* A plain button (window.Btn drops aria-*), drawn as Btn's ghost sm on each device tier */}
        <button ref={moreRef} type="button" title="More" aria-label="More" aria-haspopup="menu" aria-expanded={menu}
          onClick={() => setMenu((m) => !m)}
          onMouseEnter={(e) => { if (dv.canHover !== false) e.currentTarget.style.background = 'var(--surface-hover)'; }}
          onMouseLeave={(e) => { if (dv.canHover !== false && !menu) e.currentTarget.style.background = 'transparent'; }}
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: dv.phone ? 36 : dv.touch ? 34 : 28,
            padding: dv.phone ? '5px 12px' : dv.touch ? '5px 11px' : '5px 9px',
            border: '1px solid transparent', borderRadius: 7, background: menu ? 'var(--surface-hover)' : 'transparent',
            color: menu ? 'var(--text)' : 'var(--text-2)', cursor: 'pointer', font: 'inherit', fontSize: dv.phone ? 13.5 : dv.touch ? 12.5 : 12,
            transition: 'background .12s, color .12s' }}>
          {window.Icon && window.Icon.More ? <window.Icon.More size={14} /> : '⋯'}
        </button>
        {menu && <div aria-hidden="true" onClick={() => setMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 19 }} />}
        {menu && (
          <div ref={menuRef} role="menu" aria-label="More" onKeyDown={onMenuKey}
            style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 20, minWidth: 170, ..._lgCard,
              boxShadow: '0 8px 24px oklch(0.2 0.01 270 / 0.14)', padding: 4 }}>
            {L.has_map && (
              <button type="button" role="menuitem" disabled={!gate.ok || busy || !!live} onClick={() => extract('full')}
                style={{ ..._lgLink, display: 'block', width: '100%', padding: '7px 10px', color: gate.ok && !live ? 'var(--text)' : 'var(--text-4)', fontSize: 12.5 }}>Read all again</button>
            )}
            <button type="button" role="menuitem" onClick={() => { setMenu(false); setSettings(true); }}
              style={{ ..._lgLink, display: 'block', width: '100%', padding: '7px 10px', color: 'var(--text)', fontSize: 12.5 }}>Settings</button>
          </div>
        )}
      </div>
      {L.can_review && <window.Btn size="sm" variant="outline" disabled={busy} onClick={() => setFreezing(true)}>Lock numbers</window.Btn>}
    </div>
  );
  const whyBox = why && (
    <div style={{ ..._lgCard, padding: '8px 12px', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', fontSize: 12.5, background: 'var(--amber-bg)', color: 'var(--amber-fg)', borderColor: 'transparent' }}>
      {(why === 'ledger' ? lgUpdateLines(upd) : lgSnapshotLines(snapUpd)).map((x) => <span key={x}>{x}</span>)}
    </div>
  );
  const stripItems = [
    ['Approved', (counts.approved || 0) + (counts.overridden || 0)], ['Unconfirmed', counts.candidate || 0], ['Disputed', counts.disputed || 0],
    ['Conflicts open', dOpen], ['Checks failing', (L.checks || {}).fail || 0], ['Need a person', (L.checks || {}).needs_person || 0],
  ];
  const strip = (
    <div style={{ display: 'flex', gap: phone ? 12 : 18, flexWrap: 'wrap', fontSize: 12.5 }}>
      {stripItems.map(([label, n]) => (
        <span key={label}><span style={{ color: 'var(--text-4)' }}>{label}</span> <b style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text)' }}>{n}</b></span>
      ))}
    </div>
  );
  // Review by exception: one Approve for every clean figure, then only what needs a person.
  const rv = !frozenView ? L.review : null;
  const left = lgLeftItems(rv, dOpen);
  const reviewBar = rv && (canReview || left.length > 0) && (
    <div style={{ display: 'flex', alignItems: 'center', gap: phone ? 10 : 14, flexWrap: 'wrap', fontSize: 12.5 }}>
      {canReview && rv.clean > 0 && (
        <window.Btn size="sm" variant="primary" disabled={busy || !!live} onClick={approveClean}
          title="Every statement: read from a document with nothing against them, and the calculations on approved figures"
          icon={window.Icon && window.Icon.Check ? <window.Icon.Check size={12} /> : null}>Approve clean ({rv.clean.toLocaleString()})</window.Btn>
      )}
      {left.map((x) => (x.key === 'conflicts' ? (
        <button key={x.key} type="button" onClick={() => setSub('discrepancies')} title={x.tip}
          style={{ ..._lgLink, padding: 0, fontSize: 12.5 }}>
          <span style={{ color: 'var(--rose-fg)' }}>{x.label}</span> <b style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text)' }}>{x.n.toLocaleString()}</b>
        </button>
      ) : (
        <span key={x.key} title={x.tip}>
          <span style={{ color: 'var(--text-4)' }}>{x.label}</span> <b style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text)' }}>{x.n.toLocaleString()}</b>
        </span>
      )))}
      {rv.clean === 0 && left.length === 0 && <window.Pill hue="green" sz="sm" dot>Nothing left to review</window.Pill>}
    </div>
  );
  const subTabs = (
    <div role="tablist" style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border)' }}>
      {[['statements', 'Statements'], ['discrepancies', `Conflicts${dOpen ? ` (${dOpen})` : ''}`]].map(([k, label]) => {
        const on = sub === k;
        return (
          <button key={k} type="button" role="tab" aria-selected={on} onClick={() => setSub(k)}
            style={{ padding: '0 10px', height: phone ? 38 : 30, border: 'none', background: 'transparent', fontSize: phone ? 14 : 12.5, fontWeight: on ? 600 : 500,
              cursor: 'pointer', color: on ? 'var(--text)' : 'var(--text-3)', borderBottom: on ? '2px solid var(--text)' : '2px solid transparent', marginBottom: -1 }}>
            {label}
          </button>
        );
      })}
    </div>
  );
  const segmented = (
    <div role="tablist" aria-label="Statement"
      style={{ display: 'flex', gap: 6, overflowX: 'auto', flexWrap: phone ? 'nowrap' : 'wrap', WebkitOverflowScrolling: 'touch',
        margin: phone ? '0 -16px' : 0, padding: phone ? '0 16px 2px' : 0 }}>
      {statements.map((s) => {
        const on = stmt === s.key;
        return (
          <button key={s.key} type="button" role="tab" aria-selected={on} onClick={() => setStmt(s.key)}
            style={{ flexShrink: 0, padding: phone ? '7px 12px' : '4px 10px', borderRadius: 999, fontSize: phone ? 13.5 : 12, cursor: 'pointer', whiteSpace: 'nowrap',
              border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`, background: on ? 'var(--accent-soft)' : 'var(--surface)',
              color: on ? 'var(--accent-text)' : 'var(--text-2)', fontWeight: on ? 600 : 500 }}>
            {s.label}{!frozenView && s.approvable ? ` · ${s.approvable}` : ''}
          </button>
        );
      })}
    </div>
  );
  const g = gridFor;
  const gClean = (g && g.clean_ids) || [];
  const gMgmt = (g && g.per_management_ids) || [];
  const gridActions = canReview && g && g.snapshot == null && (gClean.length > 0 || gMgmt.length > 0 || g.not_ready_ids.length > 0 || g.ranking) && (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      {gClean.length > 0 && (
        <window.Btn size="sm" variant="primary" disabled={busy} onClick={() => approveView('clean')}
          title="Read from a document with nothing against them, and the calculations on approved figures"
          icon={window.Icon && window.Icon.Check ? <window.Icon.Check size={12} /> : null}>Approve clean ({gClean.length})</window.Btn>
      )}
      {gMgmt.length > 0 && (
        <window.Btn size="sm" variant="outline" disabled={busy} onClick={() => approveView('per_management')}
          title="Per management, estimated or outside research: not read from a document">Approve per management ({gMgmt.length})</window.Btn>
      )}
      {g.not_ready_ids.length > 0 && (
        <window.Btn size="sm" variant="outline" disabled={busy} onClick={() => setReasonFor('override_not_ready')}>Override not-Ready ({g.not_ready_ids.length})</window.Btn>
      )}
      {g.ranking && <window.Btn size="sm" variant="outline" disabled={busy} onClick={() => setReasonFor('use_ranking')}>Use new ranking</window.Btn>}
    </div>
  );

  let body = null;
  if (sub === 'statements') {
    body = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {statements.length > 0 ? segmented : null}
        {gridActions}
        {gridErr && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5, color: 'var(--rose-fg)' }}>
            <span style={{ overflowWrap: 'anywhere' }}>{gridErr}</span>
            <window.Btn size="sm" variant="outline" onClick={() => loadGrid(stmt, snapV)}>Try again</window.Btn>
          </div>
        )}
        {!statements.length ? <LgEmpty>No figures</LgEmpty>
          : !g ? (gridErr ? null : <window.SkeletonRows rows={8} />)
            : g.rows.length ? <LgGrid grid={g} monthlyOpen={monthly} onToggleMonthly={() => setMonthly((m) => !m)} onOpen={setFactId} phone={phone} />
              : <LgEmpty>No figures</LgEmpty>}
      </div>
    );
  } else {
    body = (
      <LgDiscrepancies dq={dq} err={dqErr} onRetry={loadDq} canReview={canReview} busy={busy} onResolve={resolve} onResolveMany={resolveMany}
        onReopen={reopenD} onOpen={setFactId} phone={phone} />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {failedBanner}
      {topBar}
      {whyBox}
      {strip}
      {reviewBar}
      {live && <LgLive live={live} canCancel={!!L.can_run} onCancel={cancel} phone={phone} />}
      {subTabs}
      {body}
      {factId && (
        <LgFactDrawer engagementId={engagementId} factId={factId} snapshot={snapV} canReview={canReview} phone={phone}
          onClose={() => setFactId(null)} onOpen={setFactId} onChanged={refresh} />
      )}
      {freezing && (
        <LgFreezeDrawer engagementId={engagementId} phone={phone} onClose={() => setFreezing(false)}
          onFrozen={() => { setFreezing(false); refresh(); }} />
      )}
      {settings && (
        <window.Drawer open onClose={() => setSettings(false)} title="Ledger settings" width={520} widthKey="cim-ledger-settings">
          <LgSettingsForm L={L} canEdit={!!L.can_review} busy={busy} onSave={saveSettings} phone={phone} stacked />
        </window.Drawer>
      )}
      {reasonFor && (
        <LgReasonDrawer title={reasonFor === 'use_ranking' ? 'Use new ranking' : `Override not-Ready (${g ? g.not_ready_ids.length : 0})`}
          confirmLabel={reasonFor === 'use_ranking' ? 'Use new ranking' : 'Override'} busy={busy}
          onConfirm={confirmReason} onClose={() => setReasonFor(null)} phone={phone} />
      )}
    </div>
  );
};

window.CimLedgerTab = CimLedgerTab;
// The fact drawer, read-only on a snapshot, for the Sections tab's Numbers (views/cim_sections.jsx).
window.CimFactDrawer = LgFactDrawer;
